import * as signalR from '@microsoft/signalr';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import { CONFIG } from 'src/config-global';

import { useAuthContext } from 'src/auth/hooks';

// ----------------------------------------------------------------------

/**
 * Hook for SignalR notifications hub connection
 * @returns {Object} Connection state and notifications
 */
export function useNotificationsHub() {
  const [connection, setConnection] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const connectionRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const { user } = useAuthContext();

  // Initialize SignalR connection
  useEffect(() => {
    // Don't create connection if user is not authenticated
    if (!user?.accessToken) {
      return;
    }
    const hubUrl = `${CONFIG.site.serverUrl}/api/hub/notifications`;

    const newConnection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        skipNegotiation: false,
        accessTokenFactory: () => user.accessToken,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          if (retryContext.previousRetryCount >= maxReconnectAttempts) {
            return null; // Stop reconnecting
          }
          // Exponential backoff: 2s, 4s, 8s, 16s, 32s
          return Math.min(1000 * 2 ** retryContext.previousRetryCount, 32000);
        },
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    connectionRef.current = newConnection;
    setConnection(newConnection);

    const cleanup = () => {
      if (connectionRef.current) {
        connectionRef.current.stop();
      }
    };
  }, [user?.accessToken]);

  // Start connection
  useEffect(() => {
    if (!connection) return;

    const startConnection = async () => {
      try {
        await connection.start();
        setIsConnected(true);
        reconnectAttempts.current = 0;
      } catch (error) {
        console.error('SignalR connection error:', error);
        setIsConnected(false);

        // Retry connection with exponential backoff
        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * 2 ** reconnectAttempts.current, 32000);
          reconnectAttempts.current += 1;
          setTimeout(startConnection, delay);
        }
      }
    };

    startConnection();

    // Connection closed handler
    connection.onclose((error) => {
      setIsConnected(false);
    });

    // Reconnecting handler
    connection.onreconnecting((error) => {
      setIsConnected(false);
    });

    // Reconnected handler
    connection.onreconnected((connectionId) => {
      setIsConnected(true);
      reconnectAttempts.current = 0;
    });
  }, [connection]);

  // Listen for notifications
  useEffect(() => {
    if (!connection) return;

    // Handle new notification
    const handleReceiveNotification = (notification) => {
      setNotifications((prev) => [notification, ...prev]);
    };

    // Handle notification update
    const handleNotificationUpdate = (notificationId, updates) => {
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, ...updates } : n))
      );
    };

    // Handle notification deletion
    const handleNotificationDelete = (notificationId) => {
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    };

    // Register event handlers
    connection.on('ReceiveNotification', handleReceiveNotification);
    connection.on('NotificationUpdated', handleNotificationUpdate);
    connection.on('NotificationDeleted', handleNotificationDelete);

    // Cleanup
    const cleanup = () => {
      connection.off('ReceiveNotification', handleReceiveNotification);
      connection.off('NotificationUpdated', handleNotificationUpdate);
      connection.off('NotificationDeleted', handleNotificationDelete);
    };
  }, [connection]);

  // Send notification read status
  const markAsRead = useCallback(
    async (notificationId) => {
      const _ = connection;
      const __ = isConnected;

      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n))
      );
    },
    [connection, isConnected]
  );

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    if (!connection || !isConnected) {
      console.warn('Cannot mark all as read: SignalR not connected');
      return;
    }

    try {
      await connection.invoke('MarkAllNotificationsAsRead');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, [connection, isConnected]);

  // Remove notification from local state
  const removeNotification = useCallback((notificationId) => {
    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
  }, []);

  const unreadCount = useMemo(() => notifications.filter((n) => !n.isRead).length, [notifications]);

  return {
    connection,
    isConnected,
    notifications,
    markAsRead,
    markAllAsRead,
    removeNotification,
    setNotifications,
    unreadCount,
  };
}
